import { ChevronRight } from 'lucide-react';
import { useAnalytics } from '../../../../core/analytics';
import { ConnectionSelector } from '../../common/components/ConnectionSelector';
import { DatabaseSelector } from '../../common/components/DatabaseSelector';

type StatusBarContextPickerProps = {
  connectionId?: string;
  connectionName?: string;
  serverHost?: string;
  connectionColor?: string;
  database?: string;
  onConnectionChange?: (connectionId: string, connectionName: string, serverHost: string) => void;
  onAddConnection?: () => void;
  onDatabaseChange?: (database: string) => void;
  /** Increment to pop the database list open (query-error "Switch database…" action). */
  databaseSelectorOpenNonce?: number;
};

/**
 * The Query Editor status bar's "connection › database" control: one pill
 * holding the connection and database pickers, so the pair reads as a
 * control rather than as two status labels.
 *
 * - The pill is visible at rest; each half fills on hover and has its own list.
 * - The connection's colour square leads the connection half; the host moves
 *   into its tooltip to keep the pill short.
 * - With no connection the database half is hidden instead of repeating
 *   "Not connected".
 * - A tooltip names the action only when a change handler is wired (the
 *   Visual Query Editor's results status bar has none).
 */
export function StatusBarContextPicker({
  connectionId,
  connectionName,
  serverHost,
  connectionColor,
  database,
  onConnectionChange,
  onAddConnection,
  onDatabaseChange,
  databaseSelectorOpenNonce,
}: StatusBarContextPickerProps) {
  const { track } = useAnalytics();
  const trackOpen = (picker: 'connection' | 'database') => (open: boolean) => {
    if (open) track('feature:query_context_picker_opened', { picker });
  };

  return (
    <div
      data-testid="status-bar-context-picker"
      className="flex items-center flex-shrink-0 min-w-0 h-[14px] rounded-sm bg-foreground/5 ring-1 ring-inset ring-foreground/10"
    >
      <div data-testid="status-bar-connection-section" className="flex h-full min-w-0">
        <ConnectionSelector
          connectionId={connectionId}
          connectionName={connectionName}
          serverHost={serverHost}
          color={connectionColor}
          hostPlacement="tooltip"
          actionHint={onConnectionChange ? 'Change connection for this tab' : undefined}
          onConnectionChange={onConnectionChange || (() => {})}
          onAddConnection={onAddConnection}
          onOpenChange={trackOpen('connection')}
          className="h-full text-[12px] text-foreground/80"
        />
      </div>

      {connectionId && (
        <>
          <ChevronRight aria-hidden="true" className="w-2.5 h-2.5 flex-shrink-0 text-muted-foreground" />
          <div data-testid="status-bar-database-section" className="flex h-full min-w-0">
            <DatabaseSelector
              connectionId={connectionId}
              database={database}
              onDatabaseChange={onDatabaseChange || (() => {})}
              openNonce={databaseSelectorOpenNonce}
              actionHint={onDatabaseChange ? 'Change database for this tab' : undefined}
              onOpenChange={trackOpen('database')}
              className="h-full text-[12px] text-foreground/80"
            />
          </div>
        </>
      )}
    </div>
  );
}
